import {
  forwardRef,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '../utils/cn';

export function Label({
  className,
  children,
  required,
  hint,
  ...props
}: LabelHTMLAttributes<HTMLLabelElement> & { required?: boolean; hint?: ReactNode }) {
  return (
    <label
      className={cn('flex flex-col gap-1 text-[13px] font-medium tracking-snug', className)}
      {...props}
    >
      <span className="flex items-baseline gap-1">
        <span className="text-ink">{children}</span>
        {/* Language-neutral required marker (the app is he/en/ru/fr). */}
        {required ? (
          <span className="text-clay" aria-hidden="true">
            *
          </span>
        ) : null}
      </span>
      {hint ? <span className="text-[12px] text-graphite-soft font-normal">{hint}</span> : null}
    </label>
  );
}

const inputBase =
  'block w-full bg-surface text-ink placeholder:text-graphite-soft ' +
  'border border-rule-strong hover:border-ink ' +
  'focus:border-petrol focus:outline-none focus:ring-2 focus:ring-petrol/25 ' +
  'transition-colors duration-150 px-3.5 py-2.5 text-[15px] rounded-md';

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  ({ className, ...props }, ref) => (
    <input ref={ref} className={cn(inputBase, className)} {...props} />
  ),
);
Input.displayName = 'Input';

export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, rows = 4, ...props }, ref) => (
  <textarea
    ref={ref}
    rows={rows}
    className={cn(inputBase, 'min-h-[112px] leading-[1.55] resize-y', className)}
    {...props}
  />
));
Textarea.displayName = 'Textarea';
